import { inject, InjectionToken } from '@angular/core';

import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';

import { TelemetryProperties } from './models';
import { TelemetrySink } from './interfaces';
import { TelemetryScrubberService } from './telemetry-scrubber.service';

// The only path off the device; every automatic collector is off, so a screen reports its health and nothing about the community.
export class AppInsightsSink implements TelemetrySink {
  private readonly insights: ApplicationInsights;

  public constructor(
    instrumentationKey: string,
    private readonly scrubber: TelemetryScrubberService,
    createClient: (configuration: ConstructorParameters<typeof ApplicationInsights>[0]) => ApplicationInsights
      = configuration => new ApplicationInsights(configuration)
  ) {
    this.insights = createClient({
      config: {
        instrumentationKey,
        enableAutoRouteTracking: false,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        disableExceptionTracking: true,
        enableUnhandledPromiseRejectionTracking: false,
        autoTrackPageVisitTime: false,
        disableCookiesUsage: true,
        enableSessionStorageBuffer: false,
        isStorageUseDisabled: true,
        disablePageUnloadEvents: ['beforeunload', 'unload', 'pagehide'],
        disablePageShowEvents: ['pageshow'],
        loggingLevelConsole: 0,
        loggingLevelTelemetry: 0
      }
    });

    this.insights.addTelemetryInitializer(item => this.sanitizeEnvelope(item));
    this.insights.loadAppInsights();
  }

  public sanitizeEnvelope(item: ITelemetryItem): boolean {
    const name = item.baseData ? this.scrubber.safeEventName(item.baseData['name']) : '';

    // False drops the item outright, so anything the SDK raised itself never leaves the device.
    if (item.baseType !== 'EventData' || !item.baseData || name !== item.baseData['name']) {
      return false;
    }

    item.baseData = { name, properties: this.scrubber.scrub(item.baseData['properties']) };

    // Context plugins otherwise attach the browser URL, user identity and operation name after tracking.
    item.tags = [];
    item.ext = {};
    item.data = {};

    return true;
  }

  public event(name: string, properties: TelemetryProperties): void {
    this.insights.trackEvent({ name: this.scrubber.safeEventName(name), properties: this.scrubber.scrub(properties) });
  }

  public dispose(): void {
    this.insights.unload(false);
  }
}

export const TELEMETRY_SINK_FACTORY = new InjectionToken<(key: string) => TelemetrySink>('Telemetry sink factory', {
  providedIn: 'root',
  factory: () => {
    const scrubber = inject(TelemetryScrubberService);

    return (key: string): TelemetrySink => new AppInsightsSink(key, scrubber);
  }
});
